/* El mapa del día (T3): las paradas con sitio en su orden, el hotel, la distancia a pie y los trozos
   de mapa que hay que bajar para tenerlo sin conexión. */
import { bloquesDe, type BloqueV } from '../escena/bloques.ts';
import type { Dia, Lugar, Viaje } from './tipos.ts';

export interface Punto { n: number; b: BloqueV; lugar: Lugar }

const conSitio = (l?: Lugar): l is Lugar & { lat: number; lon: number } => !!l && l.lat != null && l.lon != null && l.precision !== 'dudosa';

/** Las paradas del día que tienen sitio, numeradas en el orden del día; si dos seguidas son el mismo sitio, una sola. */
export function puntosDelDia(v: Viaje, dia: Dia): Punto[] {
  const puntos: Punto[] = [];
  for (const b of bloquesDe(dia)) {
    const l = v.lugares.find(x => x.id === b.lugar);
    if (!conSitio(l) || puntos.at(-1)?.lugar.id === l.id) continue;
    puntos.push({ n: puntos.length + 1, b, lugar: l });
  }
  return puntos;
}

/** Los sitios de la ruta del día del itinerario (y de sus QR) que no son una parada: van al mapa como marcas pequeñas. */
export const sitiosDelDia = (v: Viaje, dia: Dia): Lugar[] =>
  (dia.sitios ?? []).map(id => v.lugares.find(l => l.id === id)).filter(conSitio);

export const hotelDe = (v: Viaje, dia: Dia) => { const l = v.lugares.find(x => x.id === dia.hotel); return conSitio(l) ? l : undefined; };

/** Distancia en km entre dos puntos (lon, lat). */
export function km(a: [number, number], b: [number, number]) {
  const r = Math.PI / 180, da = (b[1] - a[1]) * r, dl = (b[0] - a[0]) * r;
  return 12742 * Math.asin(Math.sqrt(Math.sin(da / 2) ** 2 + Math.cos(a[1] * r) * Math.cos(b[1] * r) * Math.sin(dl / 2) ** 2));
}

/** A pie: las calles no van en línea recta (×1,3) y se anda a unos 4,5 km/h. */
export const minutosAPie = (d: number) => Math.round((d * 1.3) / 4.5 * 60);

/* ───────── Mapas sin conexión ───────── */
const Z_MIN = 10, Z_MAX = 14, MARGEN_KM = 2, Z_GENERAL = [5, 9];
const tx = (lon: number, z: number) => Math.floor(((lon + 180) / 360) * 2 ** z);
const ty = (lat: number, z: number) => { const r = (lat * Math.PI) / 180; return Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z); };

/** Las URL de todos los trozos de mapa del viaje, de z10 a z14: las ciudades enteras (zonas de la config del viaje)
    y, además, 2 km alrededor de cada parada, hotel y parada del coche. */
export function trozosDelViaje(v: Viaje): string[] {
  const urls = new Set<string>();
  const caja = (o: number, s: number, e: number, n: number) => {
    for (let z = Z_MIN; z <= Z_MAX; z++)
      for (let x = tx(o, z); x <= tx(e, z); x++)
        for (let y = ty(n, z); y <= ty(s, z); y++) urls.add(`/api/mapa/t/${z}/${x}/${y}.pbf`);
  };
  for (const [o, s, e, n] of Object.values(v.zonasMapa ?? {})) caja(o, s, e, n);
  // La vista general (de Kansai a Narita, z5–z9): que los días de tren o coche no salgan en blanco sin red
  const zonas = Object.values(v.zonasMapa ?? {});
  if (zonas.length) {
    const [o, s, e, n] = [Math.min(...zonas.map(z => z[0])) - .5, Math.min(...zonas.map(z => z[1])) - .5, Math.max(...zonas.map(z => z[2])) + .5, Math.max(...zonas.map(z => z[3])) + .5];
    for (let z = Z_GENERAL[0]; z <= Z_GENERAL[1]; z++)
      for (let x = tx(o, z); x <= tx(e, z); x++)
        for (let y = ty(n, z); y <= ty(s, z); y++) urls.add(`/api/mapa/t/${z}/${x}/${y}.pbf`);
  }
  // Las paradas del coche (carreteras de Gunma, Hakone, Wangan…)
  const delCoche = v.coche.dias.flatMap(d => d.paradas.map(p => v.lugares.find(l => l.id === p.lugar))).filter((l): l is Lugar => !!l && l.lat != null && l.lon != null);
  for (const l of delCoche) { const dLat = MARGEN_KM / 111, dLon = MARGEN_KM / (111 * Math.cos((l.lat! * Math.PI) / 180)); caja(l.lon! - dLon, l.lat! - dLat, l.lon! + dLon, l.lat! + dLat); }
  for (const dia of v.dias) {
    const pts = puntosDelDia(v, dia).map(p => [p.lugar.lon!, p.lugar.lat!] as [number, number]);
    const h = hotelDe(v, dia);
    if (h) pts.push([h.lon!, h.lat!]);
    for (const l of sitiosDelDia(v, dia)) pts.push([l.lon!, l.lat!]);
    // Cada sitio con su margen alrededor (no la caja de todo el día: un día Osaka–Nara sería enorme)
    for (const [lon, lat] of pts) {
      const dLat = MARGEN_KM / 111, dLon = MARGEN_KM / (111 * Math.cos((lat * Math.PI) / 180));
      for (let z = Z_MIN; z <= Z_MAX; z++)
        for (let x = tx(lon - dLon, z); x <= tx(lon + dLon, z); x++)
          for (let y = ty(lat + dLat, z); y <= ty(lat - dLat, z); y++) urls.add(`/api/mapa/t/${z}/${x}/${y}.pbf`);
    }
  }
  for (const f of ['Noto Sans Regular', 'Noto Sans Bold']) for (const r of ['0-255', '256-511', '8192-8447']) urls.add(`/api/mapa/fuentes/${encodeURIComponent(f)}/${r}.pbf`);
  return [...urls];
}

const CLAVE = 'tabiji.mapas';
export const mapasGuardados = (): { generado: string; trozos: number } | null => { try { return JSON.parse(localStorage.getItem(CLAVE) ?? 'null'); } catch { return null; } };

/** Baja los trozos (de cuatro en cuatro); el service worker los guarda. Devuelve cuántos fallaron. */
export async function descargarMapas(v: Viaje, progreso: (hechos: number, total: number) => void): Promise<number> {
  const cola = trozosDelViaje(v), total = cola.length;
  let hechos = 0, fallos = 0;
  const uno = async () => {
    for (let u = cola.shift(); u; u = cola.shift()) {
      try { const r = await fetch(u); if (!r.ok && r.status !== 204) fallos++; } catch { fallos++; }
      progreso(++hechos, total);
    }
  };
  await Promise.all([uno(), uno(), uno(), uno()]);
  if (!fallos) try { localStorage.setItem(CLAVE, JSON.stringify({ generado: v.generado, trozos: total })); } catch { /* sin sitio */ }
  return fallos;
}
